import React from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    CircularProgress,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import {
    fetchBranchDepositSlips,
    setDepositSlipFilters,
    clearDepositSlipError,
    createBranchDepositSlip,
    updateBranchDepositSlip,
    approveBranchDepositSlip,
    rejectBranchDepositSlip,
} from '../../../store/slices/branchDepositSlipsSlice';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { branchFinanceApi } from '../../../services/branchFinanceApi';
import type { BranchOption, DepositSlip } from '../types/branchFinance.types';
import GenericModal from '../../../components/GenericModal';
import { useTheme } from '../../../contexts/ThemeContext';

const statusOptions = [
    { label: 'All', value: undefined },
    { label: 'Pending', value: 0 },
    { label: 'Approved', value: 1 },
    { label: 'Rejected', value: 2 },
];

const statusColorMap: Record<number, 'warning' | 'success' | 'error'> = {
    0: 'warning',
    1: 'success',
    2: 'error',
};

const formatCurrency = (amount: number) =>
    `Rs. ${amount.toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const formatDateTime = (value?: string | null) =>
    value ? new Date(value).toLocaleString() : '—';

const BranchDepositSlipsTab: React.FC = () => {
    const dispatch = useAppDispatch();
    const { user } = useAppSelector((state) => state.auth);
    const { theme } = useTheme();
    const muiTheme = useMuiTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
    const {
        list,
        loadingList,
        error,
        branches,
        filters,
        pagination,
        submitting,
        formError,
        approveLoadingId,
        rejectLoadingId,
    } = useAppSelector((state) => state.branchDepositSlips);

    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(15);
    const [successMessage, setSuccessMessage] = React.useState<string | null>(null);
    const [formDialogOpen, setFormDialogOpen] = React.useState(false);
    const [editingSlip, setEditingSlip] = React.useState<DepositSlip | null>(null);
    const [formValues, setFormValues] = React.useState({
        slipNo: '',
        amount: '',
        depositType: 'Bank Deposit',
        attachment: null as File | null,
    });
    const [approveDialog, setApproveDialog] = React.useState({
        open: false,
        slip: null as DepositSlip | null,
        password: '',
    });
    const [rejectDialog, setRejectDialog] = React.useState({
        open: false,
        slip: null as DepositSlip | null,
        reason: '',
        attachment: null as File | null,
    });
    const [downloadError, setDownloadError] = React.useState<string | null>(null);
    const [downloadLoadingId, setDownloadLoadingId] = React.useState<number | null>(null);
    const [reasonDownloadLoadingId, setReasonDownloadLoadingId] = React.useState<number | null>(null);

    const isAdmin = user?.type === 'ADMIN';
    const isManager = user?.type === 'MANAGER';
    const totalCount = pagination?.total ?? 0;

    React.useEffect(() => {
        dispatch(
            fetchBranchDepositSlips({
                branchId: filters.branchId,
                status: filters.status,
                page: page + 1,
                perPage: rowsPerPage,
            })
        ).catch((err) => {
            console.error('Failed to fetch deposit slips:', err);
        });
    }, [dispatch, filters.branchId, filters.status, page, rowsPerPage]);

    const handleChangePage = (_: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleBranchFilterChange = (_: unknown, option: BranchOption | null) => {
        dispatch(setDepositSlipFilters({ branchId: option?.id }));
        setPage(0);
    };

    const handleStatusChange = (event: any) => {
        const value = event.target.value === '' ? undefined : Number(event.target.value);
        dispatch(setDepositSlipFilters({ status: value }));
        setPage(0);
    };

    const handleOpenForm = (slip?: DepositSlip) => {
        setEditingSlip(slip ?? null);
        setFormValues({
            slipNo: slip?.slip_no ?? '',
            amount: slip ? String(slip.amount) : '',
            depositType: slip?.deposit_type ?? 'Bank Deposit',
            attachment: null,
        });
        setFormDialogOpen(true);
        dispatch(clearDepositSlipError());
    };

    const handleCloseForm = () => {
        setFormDialogOpen(false);
        setEditingSlip(null);
        setFormValues({
            slipNo: '',
            amount: '',
            depositType: 'Bank Deposit',
            attachment: null,
        });
    };

    const handleFormSubmit = async () => {
        const payload = {
            slipNo: formValues.slipNo.trim(),
            amount: Number(formValues.amount),
            depositType: formValues.depositType,
            attachment: formValues.attachment,
        };

        if (!payload.slipNo || !payload.amount) {
            setDownloadError('Please fill all required fields.');
            return;
        }

        // Validate file for new slips
        if (!editingSlip) {
            if (!payload.attachment) {
                setDownloadError('Attachment is required for new slips.');
                return;
            }
            if (!(payload.attachment instanceof File)) {
                setDownloadError('Invalid file. Please select a valid file.');
                return;
            }
            if (payload.attachment.size === 0) {
                setDownloadError('File is empty. Please select a valid file.');
                return;
            }
        }

        let result;
        if (editingSlip) {
            result = await dispatch(
                updateBranchDepositSlip({
                    id: editingSlip.id,
                    slipNo: payload.slipNo,
                    amount: payload.amount,
                    depositType: payload.depositType,
                    attachment: payload.attachment ?? null,
                })
            );
        } else {
            result = await dispatch(
                createBranchDepositSlip({
                    slipNo: payload.slipNo,
                    amount: payload.amount,
                    depositType: payload.depositType,
                    attachment: payload.attachment!,
                })
            );
        }

        if (
            (editingSlip && updateBranchDepositSlip.fulfilled.match(result)) ||
            (!editingSlip && createBranchDepositSlip.fulfilled.match(result))
        ) {
            setSuccessMessage(`Deposit slip ${editingSlip ? 'updated' : 'created'} successfully.`);
            handleCloseForm();
            dispatch(
                fetchBranchDepositSlips({
                    branchId: filters.branchId,
                    status: filters.status,
                    page: page + 1,
                    perPage: rowsPerPage,
                })
            );
        }
    };

    const handleOpenApproveDialog = (slip: DepositSlip) => {
        setApproveDialog({ open: true, slip, password: '' });
    };

    const handleOpenRejectDialog = (slip: DepositSlip) => {
        setRejectDialog({ open: true, slip, reason: '', attachment: null });
    };

    const handleApprove = async () => {
        if (!approveDialog.slip) return;
        const result = await dispatch(
            approveBranchDepositSlip({ id: approveDialog.slip.id, password: approveDialog.password })
        );
        if (approveBranchDepositSlip.fulfilled.match(result)) {
            setApproveDialog({ open: false, slip: null, password: '' });
            setSuccessMessage('Deposit slip approved.');
        }
    };

    const handleReject = async () => {
        if (!rejectDialog.slip || !rejectDialog.reason.trim()) {
            setDownloadError('Reason is required to reject a slip.');
            return;
        }
        const result = await dispatch(
            rejectBranchDepositSlip({
                id: rejectDialog.slip.id,
                reason: rejectDialog.reason.trim(),
                attachment: rejectDialog.attachment || undefined,
            })
        );
        if (rejectBranchDepositSlip.fulfilled.match(result)) {
            setRejectDialog({ open: false, slip: null, reason: '', attachment: null });
            setSuccessMessage('Deposit slip rejected.');
        }
    };

    const getFileExtensionFromContentType = (contentType: string): string => {
        const contentTypeMap: Record<string, string> = {
            'application/pdf': 'pdf',
            'image/png': 'png',
            'image/jpeg': 'jpg',
            'image/jpg': 'jpg',
            'image/gif': 'gif',
            'image/webp': 'webp',
            'image/bmp': 'bmp',
        };
        return contentTypeMap[contentType.toLowerCase()] || 'pdf';
    };

    const handleDownload = async (slipId: number, type: 'primary' | 'reason' = 'primary') => {
        try {
            setDownloadError(null);
            if (type === 'primary') {
                setDownloadLoadingId(slipId);
            } else {
                setReasonDownloadLoadingId(slipId);
            }
            const { blob, contentType } = await branchFinanceApi.downloadDepositSlipAttachment(slipId, type);
            const extension = getFileExtensionFromContentType(contentType);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${type === 'primary' ? 'deposit_slip' : 'reason_attachment'}_${slipId}.${extension}`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setDownloadError(err instanceof Error ? err.message : 'Failed to download attachment');
        } finally {
            if (type === 'primary') {
                setDownloadLoadingId(null);
            } else {
                setReasonDownloadLoadingId(null);
            }
        }
    };

    return (
        <>
            <Card>
                <CardHeader
                    title="Deposit Slips"
                    action={
                        <Button variant="contained" onClick={() => handleOpenForm()}>
                            Add Slip
                        </Button>
                    }
                />
                <CardContent>
                    <Stack spacing={2}>
                        {successMessage && (
                            <Alert severity="success" onClose={() => setSuccessMessage(null)}>
                                {successMessage}
                            </Alert>
                        )}

                        {(error || downloadError) && (
                            <Alert
                                severity="error"
                                onClose={() => {
                                    setDownloadError(null);
                                    dispatch(clearDepositSlipError());
                                }}
                            >
                                {downloadError || error}
                            </Alert>
                        )}

                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                            {isAdmin && (
                                <Box sx={{ minWidth: { xs: '100%', md: 260 } }}>
                                    <Autocomplete
                                        options={branches}
                                        getOptionLabel={(option) => option.name}
                                        value={branches.find((branch) => branch.id === filters.branchId) || null}
                                        onChange={handleBranchFilterChange}
                                        renderInput={(params) => <TextField {...params} label='Filter by branch' />}
                                    />
                                </Box>
                            )}
                            <FormControl sx={{ minWidth: { xs: '100%', md: 200 } }}>
                                <InputLabel id="deposit-status-label">Status</InputLabel>
                                <Select
                                    labelId="deposit-status-label"
                                    label="Status"
                                    value={filters.status ?? ''}
                                    onChange={handleStatusChange}
                                >
                                    {statusOptions.map((option) => (
                                        <MenuItem key={option.label} value={option.value ?? ''}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Stack>

                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Slip #</TableCell>
                                        <TableCell>Deposit Type</TableCell>
                                        <TableCell align="right">Amount</TableCell>
                                        <TableCell>Branch</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Approved</TableCell>
                                        <TableCell>Created</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(list || []).map((slip) => (
                                        <TableRow key={slip.id}>
                                            <TableCell>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Typography>{slip.slip_no}</Typography>
                                                    {slip.has_attachment && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleDownload(slip.id, 'primary')}
                                                        >
                                                            {downloadLoadingId === slip.id ? (
                                                                <CircularProgress size={16} />
                                                            ) : (
                                                                <CloudDownloadIcon fontSize="inherit" />
                                                            )}
                                                        </IconButton>
                                                    )}
                                                    {slip.has_reason_attachment && (
                                                        <IconButton
                                                            size="small"
                                                            color="warning"
                                                            onClick={() => handleDownload(slip.id, 'reason')}
                                                        >
                                                            {reasonDownloadLoadingId === slip.id ? (
                                                                <CircularProgress size={16} />
                                                            ) : (
                                                                <CloudDownloadIcon fontSize="inherit" />
                                                            )}
                                                        </IconButton>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                            <TableCell>{slip.deposit_type}</TableCell>
                                            <TableCell align="right">{formatCurrency(slip.amount)}</TableCell>
                                            <TableCell>{slip.branch?.name ?? '—'}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={slip.status_label}
                                                    color={statusColorMap[slip.status] ?? 'warning'}
                                                    size="small"
                                                />
                                                {slip.status === 2 && slip.reason && (
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                        {slip.reason}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {slip.approved_by?.name ?? '—'}
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    {formatDateTime(slip.approved_at)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{formatDateTime(slip.created_at)}</TableCell>
                                            <TableCell align="right">
                                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                    {isManager && slip.status !== 1 && (
                                                        <IconButton size="small" onClick={() => handleOpenForm(slip)}>
                                                            <EditIcon fontSize="inherit" />
                                                        </IconButton>
                                                    )}
                                                    {isAdmin && slip.status === 0 && (
                                                        <>
                                                            <IconButton
                                                                size="small"
                                                                color="success"
                                                                onClick={() => handleOpenApproveDialog(slip)}
                                                                disabled={approveLoadingId === slip.id}
                                                            >
                                                                {approveLoadingId === slip.id ? (
                                                                    <CircularProgress size={16} />
                                                                ) : (
                                                                    <CheckIcon fontSize="inherit" />
                                                                )}
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => handleOpenRejectDialog(slip)}
                                                                disabled={rejectLoadingId === slip.id}
                                                            >
                                                                {rejectLoadingId === slip.id ? (
                                                                    <CircularProgress size={16} />
                                                                ) : (
                                                                    <CloseIcon fontSize="inherit" />
                                                                )}
                                                            </IconButton>
                                                        </>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))}

                                    {!loadingList && list.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={8}>
                                                <Typography align="center" color="text.secondary">
                                                    No deposit slips found.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {loadingList && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                <CircularProgress />
                            </Box>
                        )}

                        <TablePagination
                            component="div"
                            count={totalCount}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={[10, 15, 25, 50]}
                        />
                    </Stack>
                </CardContent>
            </Card>

            <GenericModal
                open={formDialogOpen}
                onClose={handleCloseForm}
                title={editingSlip ? 'Edit Deposit Slip' : 'New Deposit Slip'}
                maxWidth={isMobile ? 'xs' : 'sm'}
                fullWidth
                actions={
                    <>
                        <Button
                            onClick={handleCloseForm}
                            disabled={submitting}
                            fullWidth={isMobile}
                            sx={{
                                color: theme.palette.text.secondary,
                                borderRadius: 3,
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleFormSubmit}
                            variant="contained"
                            disabled={submitting}
                            fullWidth={isMobile}
                            sx={{
                                bgcolor: theme.palette.primary.main,
                                borderRadius: 3,
                                '&:hover': {
                                    bgcolor: theme.palette.primary.dark,
                                },
                            }}
                        >
                            {submitting ? (
                                <CircularProgress size={20} color="inherit" />
                            ) : (
                                'Save'
                            )}
                        </Button>
                    </>
                }
            >
                {(formError || downloadError) && (
                    <Alert
                        severity="error"
                        sx={{ mb: 2 }}
                        onClose={() => {
                            setDownloadError(null);
                            dispatch(clearDepositSlipError());
                        }}
                    >
                        {formError || downloadError}
                    </Alert>
                )}
                <Stack spacing={2}>
                    <TextField
                        label="Slip Number"
                        value={formValues.slipNo}
                        onChange={(event) => setFormValues((prev) => ({ ...prev, slipNo: event.target.value }))}
                        fullWidth
                        required
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 3,
                            },
                        }}
                    />
                    <TextField
                        label="Amount"
                        type="number"
                        value={formValues.amount}
                        onChange={(event) => setFormValues((prev) => ({ ...prev, amount: event.target.value }))}
                        fullWidth
                        required
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 3,
                            },
                        }}
                    />
                    <FormControl fullWidth>
                        <InputLabel id="deposit-type-label">Deposit Type</InputLabel>
                        <Select
                            labelId="deposit-type-label"
                            label="Deposit Type"
                            value={formValues.depositType}
                            onChange={(event) =>
                                setFormValues((prev) => ({ ...prev, depositType: event.target.value as string }))
                            }
                            sx={{
                                borderRadius: 3,
                            }}
                        >
                            <MenuItem value="Bank Deposit">Bank Deposit</MenuItem>
                            <MenuItem value="Cash in Hand">Cash in Hand</MenuItem>
                        </Select>
                    </FormControl>
                    <Button variant="outlined" component="label" fullWidth sx={{ borderRadius: 3 }}>
                        {editingSlip ? 'Replace Attachment' : 'Upload Attachment'}
                        <input
                            type="file"
                            hidden
                            onChange={(event) =>
                                setFormValues((prev) => ({
                                    ...prev,
                                    attachment: event.target.files?.[0] || null,
                                }))
                            }
                        />
                    </Button>
                    {formValues.attachment && (
                        <Typography variant="caption" color="text.secondary">
                            Selected file: {formValues.attachment.name}
                        </Typography>
                    )}
                </Stack>
            </GenericModal>

            <GenericModal
                open={approveDialog.open}
                onClose={() => setApproveDialog({ open: false, slip: null, password: '' })}
                title="Approve Deposit Slip"
                maxWidth={isMobile ? 'xs' : 'sm'}
                fullWidth
                actions={
                    <>
                        <Button
                            onClick={() => setApproveDialog({ open: false, slip: null, password: '' })}
                            disabled={approveLoadingId === approveDialog.slip?.id}
                            fullWidth={isMobile}
                            sx={{
                                color: theme.palette.text.secondary,
                                borderRadius: 3,
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleApprove}
                            variant="contained"
                            disabled={approveLoadingId === approveDialog.slip?.id}
                            fullWidth={isMobile}
                            sx={{
                                bgcolor: theme.palette.success.main,
                                borderRadius: 3,
                                '&:hover': {
                                    bgcolor: theme.palette.success.dark,
                                },
                            }}
                        >
                            {approveLoadingId === approveDialog.slip?.id ? (
                                <CircularProgress size={20} color="inherit" />
                            ) : (
                                'Approve'
                            )}
                        </Button>
                    </>
                }
            >
                <TextField
                    label="Confirm with password"
                    type="password"
                    value={approveDialog.password}
                    onChange={(event) =>
                        setApproveDialog((prev) => ({
                            ...prev,
                            password: event.target.value,
                        }))
                    }
                    fullWidth
                    autoFocus
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                        },
                    }}
                />
            </GenericModal>

            <GenericModal
                open={rejectDialog.open}
                onClose={() => setRejectDialog({ open: false, slip: null, reason: '', attachment: null })}
                title="Reject Deposit Slip"
                maxWidth={isMobile ? 'xs' : 'sm'}
                fullWidth
                actions={
                    <>
                        <Button
                            onClick={() => setRejectDialog({ open: false, slip: null, reason: '', attachment: null })}
                            disabled={rejectLoadingId === rejectDialog.slip?.id}
                            fullWidth={isMobile}
                            sx={{
                                color: theme.palette.text.secondary,
                                borderRadius: 3,
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleReject}
                            variant="contained"
                            color="error"
                            disabled={rejectLoadingId === rejectDialog.slip?.id}
                            fullWidth={isMobile}
                            sx={{
                                borderRadius: 3,
                            }}
                        >
                            {rejectLoadingId === rejectDialog.slip?.id ? (
                                <CircularProgress size={20} color="inherit" />
                            ) : (
                                'Reject'
                            )}
                        </Button>
                    </>
                }
            >
                <Stack spacing={2}>
                    <TextField
                        label="Reason"
                        multiline
                        minRows={3}
                        value={rejectDialog.reason}
                        onChange={(event) =>
                            setRejectDialog((prev) => ({
                                ...prev,
                                reason: event.target.value,
                            }))
                        }
                        required
                        fullWidth
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 3,
                            },
                        }}
                    />
                    <Button variant="outlined" component="label" fullWidth sx={{ borderRadius: 3 }}>
                        Upload Attachment (optional)
                        <input
                            type="file"
                            hidden
                            onChange={(event) =>
                                setRejectDialog((prev) => ({
                                    ...prev,
                                    attachment: event.target.files?.[0] || null,
                                }))
                            }
                        />
                    </Button>
                    {rejectDialog.attachment && (
                        <Typography variant="caption" color="text.secondary">
                            Selected file: {rejectDialog.attachment.name}
                        </Typography>
                    )}
                </Stack>
            </GenericModal>
        </>
    );
};

export default BranchDepositSlipsTab;



